import React, { useState, useContext, useEffect } from 'react';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { 
  Modal, 
  ModalDialog, 
  ModalClose, 
  Typography, 
  FormControl, 
  FormLabel, 
  Select, 
  Option, 
  Textarea,
  Grid,
  Box
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import EnhancedButton from '../EnhancedButton';
import EnhancedInput from '../EnhancedInput';
import FlatpickrDateTimeField from './FlatpickrDateTimeField';
import { TaskNotificationService } from '../../utils/TaskNotificationService';

const CreateTaskModal = ({ open, onClose, onTaskCreated, staff }) => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 2, // Default to medium
    due_date: '',
    assigned_to: ''
  });

  // Reset form whenever modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        title: '',
        description: '',
        priority: 2, // Default to medium
        due_date: '',
        assigned_to: ''
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a task title');
      return;
    }

    setLoading(true);
    try {
      // Get the next order position for the assigned user
      const { data: orderData } = await supabase
        .rpc('get_next_order_position', { 
          assigned_user_id: formData.assigned_to || null 
        });

      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
  priority: formData.priority || 2, // Default to medium if not set
        due_date: formData.due_date || null,
        assigned_to: formData.assigned_to || null,
        created_by: user.id,
        order_position: orderData || 0
      };

      const { data: createdTask, error } = await supabase
        .from('tasks')
        .insert([taskData])
        .select('id')
        .single();

      if (error) throw error;

      // Send notification if task was assigned to someone
      if (formData.assigned_to && createdTask?.id) {
        // Get assignee details for notification
        const assignee = staff.find(member => member.id === formData.assigned_to);
        if (assignee) {
          // Get current user's name for the notification
          const { data: currentUserData } = await supabase
            .from('users')
            .select('display_name')
            .eq('id', user.id)
            .single();

          await TaskNotificationService.notifyTaskAssignment(
            {
              id: createdTask.id,
              title: formData.title,
              priority: formData.priority,
              due_date: formData.due_date
            },
            assignee.email,
            assignee.display_name,
            assignee.id,
            currentUserData?.display_name || 'Someone'
          );
        }
      }

      // Reset form immediately
      setFormData({
        title: '',
        description: '',
        priority: 2,
        due_date: '',
        assigned_to: ''
      });

      // Call the parent callback to refresh tasks and close modal
      onTaskCreated();
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Error creating task: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      priority: 2,
      due_date: '',
      assigned_to: ''
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
          backdropFilter: 'blur(15px)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
      >
        <ModalClose />
        <Typography
          level="h4"
          sx={{
            mb: 3,
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '28px' }}>
            add_task
          </span>
          Create New Task
        </Typography>

        <Grid container spacing={2}>
          {/* Task Title */}
          <Grid xs={12}>
            <EnhancedInput
              label="Task Title"
              placeholder="Enter task title..."
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </Grid>

          {/* Task Description */}
          <Grid xs={12}>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Enter task description..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                minRows={3}
                maxRows={6}
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              />
            </FormControl>
          </Grid>

          {/* Priority and Due Date Row */}
          <Grid xs={12} sm={6}>
            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e, val) => setFormData({ ...formData, priority: val })}
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">None</Option>
                <Option value="1">1 (High)</Option>
                <Option value="2">2 (Medium)</Option>
                <Option value="3">3 (Low)</Option>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={6}>
            <FlatpickrDateTimeField
              label="Due Date"
              value={formData.due_date}
              onChange={val => setFormData({ ...formData, due_date: val })}
              disabled={loading}
            />
          </Grid>

          {/* Assign To */}
          <Grid xs={12}>
            <FormControl>
              <FormLabel>Assign To</FormLabel>
              <Select
                value={formData.assigned_to}
                onChange={(e, val) => setFormData({ ...formData, assigned_to: val })}
                placeholder="Select staff member (optional)"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">Leave Unassigned</Option>
                {staff.map((staffMember) => (
                  <Option key={staffMember.id} value={staffMember.id}>
                    {staffMember.display_name}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <EnhancedButton
                variant="outlined"
                onClick={handleClose}
              >
                Cancel
              </EnhancedButton>
              <EnhancedButton
                gradient
                onClick={handleSubmit}
                loading={loading}
                disabled={!formData.title.trim()}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                    add_task
                  </span>
                }
              >
                Create Task
              </EnhancedButton>
            </Box>
          </Grid>
        </Grid>
      </ModalDialog>
    </Modal>
  );
};

export default CreateTaskModal;